export interface RawDropdownOption {
  value: string
  label: string
  thumbnail?: string
}

export interface RawOptionConfig {
  name: string
  type: string
  label: string
  required?: boolean
  max_length?: number
  min_length?: number
  sort_order: number
  trimspace?: boolean
  placeholder?: string
  prompt_custom?: string
  layer_key?: string
  max_count?: number
  mapping_template?: {
    type: string
    value?: string
    action: string
    target_id: string
  }
  media_type?: string[]
  max_file_size?: number
  help_text?: string
  rows?: number
  tooltip?: string
  is_loopable?: boolean
  options?: RawDropdownOption[]
  multiple?: boolean
}

export interface RawOptionSet {
  id?: string
  name?: string
  options_config_json?: RawOptionConfig[]
}

export interface RawTemplateFrame {
  id: string
  type: string
  left: number
  top: number
  width: number
  height: number
}

export interface RawCustomSetting {
  title: string
  type: string
  value?: string
  prompt: string
}

export interface RawTemplateItem {
  id?: string
  template_id?: string
  name?: string
  title?: string
  type?: string
  image?: string
  is_music?: boolean
  option_sort_id?: string
  custom_name?: string
  custom?: RawCustomSetting[]
  canvasWidth?: number
  canvasHeight?: number
  backgroundImage?: string
  canvas_settings?: {
    width?: number
    height?: number
    backgroundImageUrl?: string
  }
  frames?: RawTemplateFrame[]
  layers?: CustomizerLayer[]
  code?: string
  tag?: string
  option_type?: string
}

export interface RawNewElement {
  top: number
  left: number
  name: string
  type: string
  angle?: number
  width: number
  height: number
  opacity?: number
  visible?: boolean
  font_size?: number
  font_family?: string
  fill?: string
  text_content?: string | null
  element_id: string
  image_options?: Array<{ src: string; label: string }>
  placeholder_url?: string | null
}

export interface RawNewLayer {
  visible: boolean
  layer_id: string
  layer_name: string
  elements: RawNewElement[]
}

export interface RawStyleItem {
  id?: string | number | null
  code?: string | null
  type?: string | null
  tag?: string | null
  is_music?: number | boolean
  option_type?: string | null
  templates?: RawTemplateConfig[]
}

export interface RawVisibilityRule {
  type: string
  actions?: Array<{ target: string; visible: boolean }>
  condition?: { field: string; value: string; operator: string }
}

export interface RawTemplateConfig {
  backgroundImage?: string
  canvasWidth?: number
  canvasHeight?: number
  option_set?: RawOptionSet
  frames?: RawTemplateFrame[]
  templates?: RawTemplateItem[]
  
  // New metadata fields
  id?: string | number
  template_id?: string
  option_set_id?: string
  canvas_settings?: {
    width: number
    height: number
    preview_background_type: string
    preview_background_value: string | null
    layers: RawNewLayer[]
  }
  ui_options?: {
    options_config: RawOptionConfig[]
    rules_config?: any[]
    is_loopable?: boolean
  }
  form_variant_key?: string | null
  direct_choice_label?: string | null
  customily_conditions?: RawFormEntry[]
  // Voice template (CMS ai_prompt_json.voice_mode): output is an audio file, not an image
  output_type?: string | null
  voice_mode?: string | null
  voice_key?: string | null
  // Template (e.g. image) that also generates a voice, shown next to the main action
  voice?: {
    voice_mode?: string | null
    voice_key?: string | null
    // Option set field names shown in the voice block, and which block comes first
    field_names?: string[] | null
    display_order?: string | null
  } | null
  option_sets?: Record<string, { options_config?: RawOptionConfig[]; rules_config?: any[]; is_loopable?: boolean }>
}

export interface RawFormEntry {
  id?: string | null
  label?: string | null
  condition?: Record<string, string>
  option_set_id?: string | null
  priority?: number | null
}

export interface RawProduct {
  id?: string
  handle?: string
}

export interface CanvasSettings {
  width: number
  height: number
  backgroundImageUrl: string
  backgroundColor?: string
}

export interface CustomizerLayer {
  id: string
  name: string
  type: 'image' | 'text' | 'audio' | 'video'
  left: number
  top: number
  width: number
  height: number
  angle?: number
  opacity?: number
  visible: boolean
  parentLayerId?: string | null
  linkedOptionId: string | null
  style?: Record<string, string | number | boolean | null>
  imageOptions?: Array<{ src: string; label: string }>
  placeholderUrl?: string | null
  textContent?: string | null
}

export interface CustomizerTemplate {
  id: string
  name: string
  title?: string
  type: string
  canvasSettings: CanvasSettings
  layers: CustomizerLayer[]
  thumbnailUrl?: string
  isMusic?: boolean
  loopConfig?: LoopConfig
  outputType?: string
  voiceMode?: string
  voiceKey?: string
  // The template has a voice to generate (voice-only template or the optional voice block)
  hasVoice?: boolean
  // Option set field names rendered in the voice block (empty: all fields stay in one block)
  voiceFieldNames?: string[]
  // Voice block is shown before the image block
  voiceFirst?: boolean
}

export interface CustomizerStyle {
  id: number | string
  code: string
  type?: string | null
  tag?: string | null
  isMusic?: boolean
  optionSortId?: string
  optionType?: string
  templates: CustomizerTemplate[]
}

export interface LoopConfig {
  enabled: boolean
  fixedCount?: number
}

export interface CustomizerOption {
  id: string
  name: string
  label: string
  type: string
  required: boolean
  placeholder: string
  defaultValue: string
  sortOrder: number
  maxLength?: number
  rows?: number
  mediaType?: string[]
  customPromptKey?: string
  helpText?: string
  tooltip?: string
  options?: RawDropdownOption[]
  multiple?: boolean
  validationRules?: {
    maxLength?: number
    mediaTypes?: string[]
    maxFileSizeMb?: number
  }
}

export interface FormEntry {
  id: string
  label: string
  condition: Record<string, string>
  optionSetId: string
  priority: number | null
}

export interface FormMeta {
  formVariantKey: string | null
  directChoiceLabel: string | null
  formEntries: FormEntry[]
  formOptionSets: Record<string, CustomizerOption[]>
}

export interface CustomizerSchema extends Partial<FormMeta> {
  productId: string
  productHandle: string
  defaultTemplateId: string
  styles: CustomizerStyle[]
  options: CustomizerOption[]
  rules: Record<string, string | number | boolean | null>[]
}

/**
 * Chọn entry có OptionSet khớp đủ điều kiện với lựa chọn variant của khách.
 * Ưu tiên: priority cao hơn → nhiều key hơn → khai báo trước.
 * Phải giữ đúng quy tắc này ở BE nếu BE tự khớp.
 * Entry không có condition (pill chọn trực tiếp) bị loại khỏi auto-match — nếu
 * không, `keys.every(...)` trên mảng rỗng sẽ luôn true và entry đó khớp bừa
 * với mọi lựa chọn. Loại pick trực tiếp chỉ chọn qua entry_id, không qua đây.
 */
export function resolveFormEntry(
  entries: FormEntry[],
  selections: Record<string, string>
): FormEntry | null {
  let best: FormEntry | null = null
  for (const entry of entries) {
    const keys = Object.keys(entry.condition)
    if (keys.length === 0) continue
    const matched = keys.every(
      (k) => (selections[k] ?? '').trim() === String(entry.condition[k]).trim()
    )
    if (!matched) continue
    if (!best) {
      best = entry
      continue
    }
    const pe = entry.priority ?? -Infinity
    const pb = best.priority ?? -Infinity
    if (pe > pb || (pe === pb && keys.length > Object.keys(best.condition).length)) {
      best = entry
    }
  }
  return best
}

export type ProductTemplateConfig = RawStyleItem[]

function isImageElementType(type: string): boolean {
  return type === 'image' || type === 'image-picker'
}

function isOptionCompatibleWithElement(opt: RawOptionConfig, el: RawNewElement): boolean {
  if (isImageElementType(el.type)) {
    return opt.type === 'MediaUploader'
  }
  if (el.type === 'text') {
    return opt.type === 'InputText' || opt.type === 'TextArea' || opt.type === 'Select'
  }
  return true
}

function findOptionForElement(
  el: RawNewElement,
  parentLayer: RawNewLayer,
  rawOptions: RawOptionConfig[]
): RawOptionConfig | undefined {
  return rawOptions.find((opt) => {
    const targetId = opt.mapping_template?.target_id
    if (targetId && targetId === el.element_id) {
      return isOptionCompatibleWithElement(opt, el)
    }
    if (opt.layer_key && opt.layer_key === el.element_id) {
      return isOptionCompatibleWithElement(opt, el)
    }
    if (!opt.layer_key || opt.layer_key !== parentLayer.layer_id) return false

    const elementsInLayer = parentLayer.elements || []
    if (opt.type === 'MediaUploader') {
      return isImageElementType(el.type)
    }
    if (opt.type === 'InputText' || opt.type === 'TextArea') {
      if (el.type !== 'text') return false
      const textElements = elementsInLayer.filter((e) => e.type === 'text')
      return textElements.length <= 1 || textElements[0]?.element_id === el.element_id
    }
    return false
  })
}

function mapOptionsFromRaw(rawOptions: RawOptionConfig[]): CustomizerOption[] {
  return rawOptions.map((opt) => ({
    id: opt.name,
    name: opt.name,
    label: opt.label || '',
    type: opt.type,
    required: !!opt.required,
    placeholder: opt.placeholder || '',
    defaultValue: '',
    sortOrder: opt.sort_order || 0,
    maxLength: opt.max_length,
    mediaType: opt.media_type,
    customPromptKey: opt.prompt_custom || undefined,
    helpText: opt.help_text,
    tooltip: opt.tooltip,
    options: opt.options,
    multiple: opt.multiple,
    validationRules: {
      maxLength: opt.max_length,
      mediaTypes: opt.media_type,
      maxFileSizeMb: opt.max_file_size
        ? Math.round(opt.max_file_size / 1048576)
        : undefined,
    },
  }))
}

function flattenCanvasLayers(
  canvasLayers: RawNewLayer[] | undefined,
  rawOptions: RawOptionConfig[]
): CustomizerLayer[] {
  const layers: CustomizerLayer[] = []
  if (!canvasLayers) return layers

  canvasLayers.forEach((layer) => {
    if (!layer.elements) return
    layer.elements.forEach((el) => {
      const linkedOpt = findOptionForElement(el, layer, rawOptions)
      layers.push({
        id: el.element_id,
        name: el.name,
        type: el.type === 'text' ? 'text' : 'image',
        left: el.left,
        top: el.top,
        width: el.width,
        height: el.height,
        angle: el.angle || 0,
        opacity: el.opacity ?? 1,
        visible: el.visible !== false && layer.visible !== false,
        parentLayerId: layer.layer_id,
        linkedOptionId: linkedOpt ? linkedOpt.name : null,
        style: {
          fontSize: el.font_size ?? null,
          fontFamily: el.font_family ?? null,
          fill: el.fill ?? null,
        },
        imageOptions: el.image_options,
        placeholderUrl: el.placeholder_url,
        textContent: el.text_content,
      })
    })
  })

  return layers
}

function mapRulesFromRaw(
  rules?: RawVisibilityRule[]
): Record<string, string | number | boolean | null>[] {
  if (!rules?.length) return []
  return rules.map((rule) => ({
    type: rule.type,
    target: rule.actions?.[0]?.target ?? null,
    visible: rule.actions?.[0]?.visible ?? null,
    conditionField: rule.condition?.field ?? null,
    conditionOperator: rule.condition?.operator ?? null,
    conditionValue: rule.condition?.value ?? null,
  }))
}

function mapNewFormatTemplate(
  template: RawTemplateConfig,
  styleMeta?: RawStyleItem,
  loopEnabled = false
): {
  template: CustomizerTemplate
  options: CustomizerOption[]
  rules: Record<string, string | number | boolean | null>[]
  formMeta: FormMeta
} {
  const rawOptions = template.ui_options?.options_config || []
  const options = mapOptionsFromRaw(rawOptions)

  // Variant-matched entries (có condition) chỉ giữ lại khi có option_set_id — nếu
  // không thì chúng không đổi được gì ở FE nên bỏ qua. Entry direct-pick (condition
  // rỗng — pill) luôn giữ lại dù không có option_set_id: pill có thể chỉ đổi prompt
  // (BE tự xử lý), không nhất thiết phải đổi form.
  const formEntries: FormEntry[] = (template.customily_conditions || [])
    .filter((e) => !!e.option_set_id || Object.keys(e.condition || {}).length === 0)
    .map((e) => ({
      id: e.id || '',
      label: e.label || '',
      condition: e.condition || {},
      optionSetId: e.option_set_id || '',
      priority: e.priority ?? null,
    }))
  const formOptionSets: Record<string, CustomizerOption[]> = {}
  Object.entries(template.option_sets || {}).forEach(([id, os]) => {
    formOptionSets[id] = mapOptionsFromRaw(os.options_config || [])
  })
  const formMeta: FormMeta = {
    formVariantKey: template.form_variant_key ?? null,
    directChoiceLabel: template.direct_choice_label ?? null,
    formEntries,
    formOptionSets,
  }
  const layers = flattenCanvasLayers(template.canvas_settings?.layers, rawOptions)
  const canvas = template.canvas_settings

  const templateLoopEnabled = !!(template.ui_options?.is_loopable || loopEnabled)

  return {
    template: {
      id: template.template_id || '',
      name: template.template_id || '',
      type: styleMeta?.type || '',
      canvasSettings: {
        width: canvas?.width || 1000,
        height: canvas?.height || 1000,
        backgroundImageUrl: canvas?.preview_background_value || '',
      },
      layers,
      loopConfig: {
        enabled: templateLoopEnabled,
      },
      outputType: template.output_type || undefined,
      voiceMode: template.voice_mode || template.voice?.voice_mode || undefined,
      voiceKey: template.voice_key || template.voice?.voice_key || undefined,
      hasVoice: template.output_type === 'voice' || !!template.voice || undefined,
      voiceFieldNames: template.voice?.field_names?.length ? template.voice.field_names : undefined,
      voiceFirst: template.voice?.display_order === 'voice_first' || undefined,
    },
    options,
    rules: mapRulesFromRaw(template.ui_options?.rules_config),
    formMeta,
  }
}

function mapStylesArray(
  stylesRaw: RawStyleItem[],
  product: RawProduct | null | undefined,
  loopEnabled = false
): CustomizerSchema {
  const styles: CustomizerStyle[] = []
  let options: CustomizerOption[] = []
  let rules: Record<string, string | number | boolean | null>[] = []
  let formMeta: FormMeta = { formVariantKey: null, directChoiceLabel: null, formEntries: [], formOptionSets: {} }

  stylesRaw.forEach((styleItem) => {
    const templates: CustomizerTemplate[] = []

    ;(styleItem.templates || []).forEach((t) => {
      if (!t.canvas_settings || !t.ui_options) return
      const mapped = mapNewFormatTemplate(t, styleItem, loopEnabled)
      templates.push(mapped.template)
      if (!options.length) {
        options = mapped.options
        rules = mapped.rules
        formMeta = mapped.formMeta
      }
    })

    if (!templates.length) return

    const styleId =
      styleItem.id ?? styleItem.code ?? templates[0].id ?? 'default-style'

    styles.push({
      id: styleId,
      code: styleItem.code || product?.handle || '',
      type: styleItem.type,
      tag: styleItem.tag,
      isMusic: !!styleItem.is_music,
      optionType: styleItem.option_type ?? undefined,
      templates,
    })
  })

  const defaultTemplateId =
    styles.length > 0 && styles[0].templates.length > 0
      ? styles[0].templates[0].id
      : 'default-template'

  return {
    productId: product?.id || '',
    productHandle: product?.handle || '',
    defaultTemplateId,
    styles,
    options,
    rules,
    ...formMeta,
  }
}

function emptyCustomizerSchema(product?: RawProduct | null): CustomizerSchema {
  return {
    productId: product?.id || '',
    productHandle: product?.handle || '',
    defaultTemplateId: '',
    styles: [],
    options: [],
    rules: [],
    formVariantKey: null,
    directChoiceLabel: null,
    formEntries: [],
    formOptionSets: {},
  }
}

/**
 * Transforms raw backend metadata to the unified frontend customization schema.
 */
export function mapRawMetadataToCustomizerSchema(
  raw: RawTemplateConfig | RawStyleItem[] | null | undefined,
  product: RawProduct | null | undefined
): CustomizerSchema {
  if (!raw) return emptyCustomizerSchema(product)

  let loopEnabled = false
  if (Array.isArray(raw)) {
    loopEnabled = raw.some((styleItem: any) =>
      styleItem.templates?.some((t: any) => !!t.ui_options?.is_loopable)
    )
  } else {
    loopEnabled = !!raw.ui_options?.is_loopable
  }

  if (Array.isArray(raw)) {
    return mapStylesArray(raw, product, loopEnabled)
  }

  if (raw.canvas_settings && raw.ui_options) {
    return mapStylesArray([{ templates: [raw] }], product, loopEnabled)
  }

  const rawOptions = raw?.option_set?.options_config_json || []
  const options: CustomizerOption[] = rawOptions.map((opt) => ({
    id: opt.name,
    name: opt.name,
    label: opt.label || '',
    type: opt.type,
    required: !!opt.required,
    placeholder: opt.placeholder || '',
    defaultValue: '',
    sortOrder: opt.sort_order || 0,
    maxLength: opt.max_length,
    rows: opt.rows,
    mediaType: opt.media_type,
    customPromptKey: opt.prompt_custom || undefined,
    helpText: opt.help_text,
    tooltip: opt.tooltip,
    options: opt.options,
    multiple: opt.multiple,
    validationRules: {
      maxLength: opt.max_length,
      mediaTypes: opt.media_type,
    },
  }))

  const styles: CustomizerStyle[] = []

  if (raw?.templates && Array.isArray(raw.templates) && raw.templates.length > 0) {
    raw.templates.forEach((t) => {
      const templateId = t.template_id || t.id || 'default-template'
      const templateName = t.name || t.title || 'Template'
      
      const width = t.canvas_settings?.width || t.canvasWidth || raw?.canvasWidth || 1000
      const height = t.canvas_settings?.height || t.canvasHeight || raw?.canvasHeight || 1000
      const backgroundImageUrl = t.canvas_settings?.backgroundImageUrl || t.backgroundImage || raw?.backgroundImage || ''

      const rawFrames = t.frames || raw?.frames || []
      const layers: CustomizerLayer[] = rawFrames.map((frame) => ({
        id: frame.id,
        name: frame.id,
        type: frame.type === 'text' ? 'text' : 'image',
        left: frame.left,
        top: frame.top,
        width: frame.width,
        height: frame.height,
        visible: true,
        linkedOptionId: frame.id,
      }))

      const templateLoopEnabled = !!(raw?.ui_options?.is_loopable || loopEnabled)

      const templateObj: CustomizerTemplate = {
        id: templateId,
        name: templateName,
        title: t.title,
        type: t.type || 'standard',
        canvasSettings: {
          width,
          height,
          backgroundImageUrl,
        },
        layers,
        thumbnailUrl: t.image,
        isMusic: t.is_music,
        loopConfig: {
          enabled: templateLoopEnabled,
        },
      }

      const styleId = t.id || 'default-style'
      let existingStyle = styles.find(s => s.id === styleId)
      if (!existingStyle) {
        existingStyle = {
          id: styleId,
          code: t.code || '',
          type: t.type || null,
          tag: t.tag || null,
          isMusic: t.is_music,
          optionSortId: t.option_sort_id,
          optionType: t.option_type,
          templates: []
        }
        styles.push(existingStyle)
      }
      existingStyle.templates.push(templateObj)
    })
  } else {
    // Fallback to legacy single template structure
    const rawFrames = raw?.frames || []
    const layers: CustomizerLayer[] = rawFrames.map((frame) => ({
      id: frame.id,
      name: frame.id,
      type: frame.type === 'text' ? 'text' : 'image',
      left: frame.left,
      top: frame.top,
      width: frame.width,
      height: frame.height,
      visible: true,
      linkedOptionId: frame.id,
    }))

    const templates: CustomizerTemplate[] = [{
      id: raw?.option_set?.id || 'default-template',
      name: raw?.option_set?.name || 'Default Template',
      type: 'standard',
      canvasSettings: {
        width: raw?.canvasWidth || 1000,
        height: raw?.canvasHeight || 1000,
        backgroundImageUrl: raw?.backgroundImage || '',
      },
      layers,
    }]

    styles.push({
      id: raw?.option_set?.id || 'default-style',
      code: product?.handle || '',
      type: 'standard',
      templates
    })
  }

  const defaultTemplateId = styles.length > 0 && styles[0].templates.length > 0 ? styles[0].templates[0].id : 'default-template'

  return {
    productId: product?.id || '',
    productHandle: product?.handle || '',
    defaultTemplateId,
    styles,
    options,
    rules: [],
  }
}

export interface CustomizerSchemaPreview {
  productId: string            // ID của sản phẩm
  productHandle: string        // Handle của sản phẩm từ Shopify
  defaultTemplateId: string    // ID của template mặc định sẽ load ban đầu
  
  // Danh sách các Template (Standard hoặc AI) của sản phẩm
  templates: {
    id: string                 // ID định danh duy nhất của template
    name: string               // Tên hiển thị của template
    title?: string             // Tiêu đề (nếu có)
    type: string               // Loại template ('standard' | 'ai_style_transfer')
    canvasSettings: {
      width: number            // Chiều rộng canvas
      height: number           // Chiều cao canvas
      backgroundImageUrl: string // Đường dẫn ảnh nền sản phẩm
      backgroundColor?: string // Màu nền canvas (tùy chọn)
    }
    layers: {
      id: string               // ID layer (khớp với option name để đồng bộ dữ liệu)
      name: string             // Tên layer
      type: 'image' | 'text' | 'audio' | 'video'
      left: number
      top: number
      width: number
      height: number
      angle?: number
      opacity?: number
      visible: boolean
      linkedOptionId: string | null
      style?: Record<string, string | number | boolean | null>
    }[]
    thumbnailUrl?: string      // Ảnh đại diện của phong cách AI
    isMusic?: boolean          // AI template hỗ trợ sinh nhạc
  }[]

  // Danh sách các Option nhập liệu trên giao diện Form tùy biến
  options: {
    id: string                 // ID option
    name: string               // Tên duy nhất dùng làm key trong personalizationValues
    label: string              // Nhãn hiển thị trên form UI
    type: string               // Loại trường (InputText, TextArea, MediaUploader, Select, ...)
    required: boolean          // Bắt buộc nhập hay không
    placeholder: string        // Gợi ý nhập liệu
    defaultValue: string       // Giá trị mặc định
    sortOrder: number          // Thứ tự sắp xếp hiển thị trên form
    maxLength?: number         // Độ dài chuỗi tối đa
    rows?: number              // Số hàng hiển thị (TextArea)
    mediaType?: string[]       // Định dạng file cho phép (MediaUploader)
    customPromptKey?: string   // Key định danh map giá trị gửi lên payload AI
    validationRules?: {
      maxLength?: number
      mediaTypes?: string[]
      maxFileSizeMb?: number
    }
  }[]

  // Danh sách quy luật hiển thị ẩn/hiện động (Interactive Rules) giữa các trường
  rules: Record<string, string | number | boolean | null>[]
}